import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { PATHS } from 'routes/routes.constants';

import Clickable from '@polyai/ui/components/atoms/Clickable';
import {
  LeftNavCloseIcon,
  LeftNavOpenIcon,
  SearchIcon,
} from '@polyai/ui/components/atoms/Icons';
import {
  ShadSidebar,
  ShadSidebarContent,
  ShadSidebarGroup,
  ShadSidebarMenu,
  ShadSidebarMenuButton,
  ShadSidebarMenuItem,
  ShadSidebarProvider,
  useShadSidebar,
} from '@polyai/ui/components/organisms/ShadSidebar';
import { cn } from '@polyai/ui/utils';

import { useAppSelector } from 'hooks/reduxHooks';
import useAccounts from 'hooks/useAccounts';
import useProjectRoute from 'hooks/useProjectRoute';
import { generateNextPath } from 'lib/routeHelper';
import { AccountAbbreviation } from './components/AccountAbbreviation';
import { IASidebarFooter } from './components/IASidebarFooter';
import type { SidebarAccount } from './types';
import { getAccountAbbreviation, getAccountLabel } from './utils';

export type { SidebarAccount } from './types';
export { getAccountAbbreviation } from './utils';

const HOVER_DEBOUNCE_MS = 300;

export const IAAccountSidebar = () => {
  const { accountId } = useProjectRoute();
  const { accounts } = useAccounts();
  const user = useAppSelector(state => state.auth.user);
  const logout = useAppSelector(state => state.auth.logout);
  const [searchValue, setSearchValue] = useState('');
  const [open, setOpen] = useState(false);

  const sidebarAccounts = useMemo(
    () =>
      accounts
        .map(account => ({
          id: account.id,
          name: getAccountLabel(account.name, user?.email),
          abbreviation: getAccountAbbreviation(account.name, user?.email),
        }))
        .filter(account =>
          account.name.toLowerCase().includes(searchValue.toLowerCase()),
        ),
    [accounts, searchValue, user?.email],
  );

  return (
    <ShadSidebarProvider
      className="h-full w-auto min-h-0"
      open={open}
      onOpenChange={setOpen}
    >
      <IAAccountSidebarContent
        accounts={sidebarAccounts}
        accountId={accountId}
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        userEmail={user?.email}
        logout={logout as (() => void) | undefined}
      />
    </ShadSidebarProvider>
  );
};

type IAAccountSidebarContentProps = {
  accounts: SidebarAccount[];
  accountId: string;
  searchValue: string;
  setSearchValue: (value: string) => void;
  userEmail?: string;
  logout?: () => void;
};

const IAAccountSidebarContent = ({
  accounts,
  accountId,
  searchValue,
  setSearchValue,
  userEmail,
  logout,
}: IAAccountSidebarContentProps) => {
  const { setOpen, open, toggleSidebar } = useShadSidebar();
  const searchInputRef = useRef<HTMLInputElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isMouseOver, setIsMouseOver] = useState(false);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const handleMouseEnter = useCallback(() => setIsMouseOver(true), []);
  const handleMouseLeave = useCallback(() => setIsMouseOver(false), []);
  const handleSearchFocus = useCallback(() => setIsSearchFocused(true), []);
  const handleSearchBlur = useCallback(() => setIsSearchFocused(false), []);
  const handleDropdownOpenChange = useCallback(
    (nextOpen: boolean) => setIsDropdownOpen(nextOpen),
    [],
  );

  useEffect(() => {
    const shouldExpand = isMouseOver || isSearchFocused || isDropdownOpen;

    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    timerRef.current = setTimeout(() => {
      setOpen(shouldExpand);
      if (!shouldExpand) {
        setSearchValue('');
      }
    }, HOVER_DEBOUNCE_MS);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [isMouseOver, isSearchFocused, isDropdownOpen, setOpen, setSearchValue]);

  useEffect(() => {
    if (!open) {
      searchInputRef.current?.blur();
    }
  }, [open]);

  return (
    <div
      className="h-full"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <ShadSidebar
        collapsible="icon"
        className="h-full [&>[data-sidebar=sidebar]]:bg-background-page"
      >
        <div className="flex h-md3 items-center justify-center border-b border-border-subtle px-xs3 py-sm1">
          <button
            aria-label="Open search"
            className="shrink-0 group-data-[state=expanded]:hidden !inline-flex cursor-pointer bg-transparent border-none p-0"
            onClick={() => setOpen(true)}
            type="button"
          >
            <SearchIcon
              className="px-xs2 py-[6px]"
              color="iconSecondary"
              size="md"
            />
          </button>
          <label className="relative flex min-w-0 flex-1 group-data-[collapsible=icon]:hidden">
            <input
              ref={searchInputRef}
              aria-label="Search accounts"
              className="h-sm4 w-full rounded-small border border-border-subtle bg-background mr-xs1 ml-xs2 text-body-regular text-primary outline-none transition-colors placeholder:text-placeholder focus:border-border-strong"
              onChange={event => setSearchValue(event.target.value)}
              onFocus={handleSearchFocus}
              onBlur={handleSearchBlur}
              placeholder="Search accounts"
              type="search"
              value={searchValue}
            />
          </label>
          <button
            aria-label={open ? 'Collapse sidebar' : 'Expand sidebar'}
            className="inline-flex size-[28px] shrink-0 items-center justify-center rounded-xSmall transition-colors hover:bg-background-hover group-data-[collapsible=icon]:hidden"
            data-test-id="ia-account-sidebar-trigger"
            type="button"
            onClick={toggleSidebar}
          >
            {open ? (
              <LeftNavCloseIcon size="sm" color="iconSecondary" />
            ) : (
              <LeftNavOpenIcon size="sm" color="iconSecondary" />
            )}
          </button>
        </div>

        <ShadSidebarContent>
          <ShadSidebarGroup className="mt-sm1 p-0">
            <ShadSidebarMenu className="gap-xs2">
              {accounts.map(account => {
                const isActive = account.id === accountId;

                return (
                  <ShadSidebarMenuItem key={account.id} className="mx-xs3">
                    <ShadSidebarMenuButton
                      isActive={isActive}
                      className={cn(
                        'hover:bg-background-page',
                        'group-data-[collapsible=icon]:!h-[40px] group-data-[collapsible=icon]:!w-[40px] group-data-[collapsible=icon]:!p-xs1',
                        isActive &&
                          '[data-active=true]:bg-background-accent-strong',
                      )}
                      render={
                        <Clickable
                          to={generateNextPath(PATHS.ASSISTANTS, {
                            accountId: account.id,
                          })}
                        />
                      }
                      data-test-id={`ia-account-sidebar-item-${account.id}`}
                    >
                      <AccountAbbreviation
                        abbreviation={account.abbreviation}
                        isActive={isActive}
                        className="group-data-[state=expanded]:hidden"
                      />
                      <span className="truncate group-data-[collapsible=icon]:w-0 group-data-[collapsible=icon]:overflow-hidden">
                        {account.name}
                      </span>
                    </ShadSidebarMenuButton>
                  </ShadSidebarMenuItem>
                );
              })}
            </ShadSidebarMenu>
          </ShadSidebarGroup>
        </ShadSidebarContent>

        <IASidebarFooter
          userEmail={userEmail}
          logout={logout}
          onDropdownOpenChange={handleDropdownOpenChange}
        />
      </ShadSidebar>
    </div>
  );
};
